import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  AvatarComponent,
  Badge,
  Progress,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@learn-me/ui";
import { adminApi } from "@/features/admin/api/adminApi";
import { formatDate, formatCurrency } from "@learn-me/utils";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Mail,
  Calendar,
  BookOpen,
  CreditCard,
  HelpCircle,
  CheckCircle,
  XCircle,
  Clock,
  Pencil,
  UserX,
  UserCheck,
  PlusCircle,
  Trash2,
  Award,
  Package,
} from "lucide-react";
import {
  StudentStatusBadge,
  EditStudentModal,
  DeactivateStudentModal,
  EnrollStudentModal,
} from "@/features/admin/components/students";
import { normalizeImageUrl } from "@/lib/imageUrl";

export default function StudentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);

  const { data: student, isLoading } = useQuery({
    queryKey: ["admin-student-detail-enhanced", id],
    queryFn: () => adminApi.getStudentDetailEnhanced(id!),
    enabled: !!id,
  });

  const activateMutation = useMutation({
    mutationFn: () => adminApi.activateStudent(id!),
    onSuccess: () => {
      toast.success("Cuenta activada exitosamente");
      queryClient.invalidateQueries({
        queryKey: ["admin-student-detail-enhanced", id],
      });
      queryClient.invalidateQueries({ queryKey: ["admin-students-enhanced"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al activar la cuenta");
    },
  });

  const cancelEnrollmentMutation = useMutation({
    mutationFn: (enrollmentId: string) =>
      adminApi.cancelEnrollment(enrollmentId),
    onSuccess: () => {
      toast.success("Inscripcion cancelada");
      queryClient.invalidateQueries({
        queryKey: ["admin-student-detail-enhanced", id],
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al cancelar la inscripcion");
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-1/4 mb-4" />
          <div className="grid grid-cols-3 gap-6">
            <div className="h-64 bg-muted rounded" />
            <div className="col-span-2 h-64 bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="text-center py-16">
        <h2 className="text-xl font-semibold mb-2">Estudiante no encontrado</h2>
        <Link to="/admin/students">
          <Button variant="outline">Volver a estudiantes</Button>
        </Link>
      </div>
    );
  }

  const fullName = `${student.firstName} ${student.lastName}`.trim();
  const coursesCompleted = student.enrollments.filter(
    (e) => e.progress === 100,
  ).length;
  const quizzesPassed = student.quizAttempts.filter(
    (q) => q.status === "PASSED",
  ).length;
  const quizzesFailed = student.quizAttempts.filter(
    (q) => q.status === "FAILED",
  ).length;
  const existingCourseIds = student.enrollments.map((e) => e.course.id);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/students">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold">{fullName}</h1>
              <StudentStatusBadge isActive={student.isActive} />
            </div>
            <p className="text-muted-foreground">{student.email}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEditModalOpen(true)}>
            <Pencil className="h-4 w-4 mr-2" />
            Editar
          </Button>
          {student.isActive ? (
            <Button
              variant="outline"
              className="text-destructive hover:text-destructive"
              onClick={() => setDeactivateModalOpen(true)}
            >
              <UserX className="h-4 w-4 mr-2" />
              Desactivar
            </Button>
          ) : (
            <Button
              variant="outline"
              className="text-green-600 hover:text-green-600"
              onClick={() => activateMutation.mutate()}
              disabled={activateMutation.isPending}
            >
              <UserCheck className="h-4 w-4 mr-2" />
              {activateMutation.isPending ? "Activando..." : "Activar"}
            </Button>
          )}
        </div>
      </div>

      {/* Deactivation notice */}
      {!student.isActive && student.deactivatedAt && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <UserX className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">
                  Cuenta desactivada
                </p>
                <p className="text-sm text-yellow-700">
                  Desactivada el {formatDate(new Date(student.deactivatedAt))}
                  {student.deactivatedReason && (
                    <>. Razon: {student.deactivatedReason}</>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="summary" className="space-y-4">
        <TabsList>
          <TabsTrigger value="summary">Resumen</TabsTrigger>
          <TabsTrigger value="courses">
            Cursos ({student.enrollments.length})
          </TabsTrigger>
          <TabsTrigger value="payments">
            Pagos ({student.payments.length})
          </TabsTrigger>
          <TabsTrigger value="activity">Actividad</TabsTrigger>
        </TabsList>

        {/* Summary Tab */}
        <TabsContent value="summary" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Profile Card */}
            <Card>
              <CardContent className="pt-6 text-center">
                <AvatarComponent
                  src={student.avatarUrl ?? undefined}
                  alt={fullName}
                  fallback={student.firstName?.charAt(0) || "?"}
                  size="xl"
                  className="mx-auto mb-4"
                />
                <h2 className="text-xl font-semibold">{fullName}</h2>
                <div className="flex items-center justify-center gap-2 text-muted-foreground mt-1">
                  <Mail className="h-4 w-4" />
                  <span className="text-sm">{student.email}</span>
                </div>
                <div className="flex items-center justify-center gap-2 text-muted-foreground mt-1">
                  <Calendar className="h-4 w-4" />
                  <span className="text-sm">
                    Registrado: {formatDate(new Date(student.createdAt))}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Stats Card */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <h3 className="font-semibold">Estadisticas</h3>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <p className="text-3xl font-bold text-primary">
                      {formatCurrency(student.totalSpent, "USD")}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Total gastado
                    </p>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <p className="text-3xl font-bold">
                      {student.enrollments.length}
                    </p>
                    <p className="text-sm text-muted-foreground">Cursos</p>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <p className="text-3xl font-bold text-green-600">
                      {quizzesPassed}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Quizzes aprobados
                    </p>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <p className="text-3xl font-bold">{coursesCompleted}</p>
                    <p className="text-sm text-muted-foreground">Completados</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Badges */}
          {student.badges.length > 0 && (
            <Card>
              <CardHeader className="flex flex-row items-center gap-2">
                <Award className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">
                  Insignias ({student.badges.length})
                </h3>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {student.badges.map((badge) => (
                    <div
                      key={badge.id}
                      className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg"
                    >
                      {badge.badge.icon && (
                        <span className="text-2xl">{badge.badge.icon}</span>
                      )}
                      <div>
                        <p className="font-medium text-sm">
                          {badge.badge.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {badge.badge.course.title}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Courses Tab */}
        <TabsContent value="courses" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Cursos Inscritos</h3>
            <Button onClick={() => setEnrollModalOpen(true)}>
              <PlusCircle className="h-4 w-4 mr-2" />
              Inscribir a Curso
            </Button>
          </div>

          {student.enrollments.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  Sin inscripciones
                </h3>
                <p className="text-muted-foreground mb-4">
                  Este estudiante aun no esta inscrito en ningun curso.
                </p>
                <Button onClick={() => setEnrollModalOpen(true)}>
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Inscribir a Curso
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {student.enrollments.map((enrollment) => (
                <Card key={enrollment.id}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-20 h-12 bg-muted rounded overflow-hidden flex-shrink-0">
                      {enrollment.course.thumbnailUrl ? (
                        <img
                          src={
                            normalizeImageUrl(enrollment.course.thumbnailUrl) ??
                            ""
                          }
                          alt={enrollment.course.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookOpen className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">
                          {enrollment.course.title}
                        </h4>
                        <Badge
                          variant={
                            enrollment.status === "ACTIVE"
                              ? "default"
                              : enrollment.status === "COMPLETED"
                                ? "success"
                                : "secondary"
                          }
                          className="text-xs"
                        >
                          {enrollment.status === "ACTIVE" && "Activo"}
                          {enrollment.status === "COMPLETED" && "Completado"}
                          {enrollment.status === "CANCELLED" && "Cancelado"}
                          {enrollment.status === "EXPIRED" && "Expirado"}
                        </Badge>
                        {enrollment.source !== "SELF" && (
                          <Badge variant="outline" className="text-xs">
                            {enrollment.source === "ADMIN" && "Manual"}
                            {enrollment.source === "BUNDLE" && "Paquete"}
                            {enrollment.source === "INVITATION" && "Invitación"}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Inscrito: {formatDate(new Date(enrollment.enrolledAt))}
                        {enrollment.expiresAt && (
                          <>
                            {" "}
                            | Expira:{" "}
                            {formatDate(new Date(enrollment.expiresAt))}
                          </>
                        )}
                      </p>
                      {enrollment.enrollmentNote && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Nota: {enrollment.enrollmentNote}
                        </p>
                      )}
                    </div>
                    <div className="w-32">
                      <div className="flex items-center gap-2">
                        <Progress
                          value={enrollment.progress}
                          className="h-2 flex-1"
                        />
                        <span className="text-sm font-medium w-10">
                          {enrollment.progress}%
                        </span>
                      </div>
                      {enrollment.lastAccessedAt && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Último:{" "}
                          {formatDate(new Date(enrollment.lastAccessedAt))}
                        </p>
                      )}
                    </div>
                    {enrollment.status === "ACTIVE" &&
                      enrollment.source === "ADMIN" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => {
                            if (confirm("¿Cancelar esta inscripcion?")) {
                              cancelEnrollmentMutation.mutate(enrollment.id);
                            }
                          }}
                          disabled={cancelEnrollmentMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Bundle Enrollments */}
          {student.bundleEnrollments.length > 0 && (
            <>
              <h3 className="text-lg font-semibold mt-8">Paquetes</h3>
              <div className="space-y-4">
                {student.bundleEnrollments.map((enrollment) => (
                  <Card key={enrollment.id}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="p-3 bg-muted rounded-lg">
                        <Package className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">
                            {enrollment.bundle.name}
                          </h4>
                          <Badge variant="default" className="text-xs">
                            {enrollment.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Inscrito:{" "}
                          {formatDate(new Date(enrollment.enrolledAt))}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <Card>
            <CardHeader className="flex flex-row items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Historial de Pagos</h3>
            </CardHeader>
            <CardContent>
              {student.payments.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No hay pagos registrados
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 font-medium">Curso</th>
                        <th className="text-left py-2 font-medium">Monto</th>
                        <th className="text-left py-2 font-medium">Estado</th>
                        <th className="text-left py-2 font-medium">Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {student.payments.map((payment) => (
                        <tr key={payment.id} className="border-b last:border-0">
                          <td className="py-3">{payment.course.title}</td>
                          <td className="py-3 font-medium">
                            {formatCurrency(payment.amount, payment.currency)}
                          </td>
                          <td className="py-3">
                            <Badge
                              variant={
                                payment.status === "COMPLETED"
                                  ? "success"
                                  : payment.status === "PENDING"
                                    ? "outline"
                                    : "destructive"
                              }
                            >
                              {payment.status === "COMPLETED" && "Completado"}
                              {payment.status === "PENDING" && "Pendiente"}
                              {payment.status === "FAILED" && "Fallido"}
                              {payment.status === "REFUNDED" && "Reembolsado"}
                            </Badge>
                          </td>
                          <td className="py-3 text-muted-foreground">
                            {payment.completedAt
                              ? formatDate(new Date(payment.completedAt))
                              : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="space-y-6">
          {/* Quiz Attempts */}
          <Card>
            <CardHeader className="flex flex-row items-center gap-2">
              <HelpCircle className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">
                Intentos de Quiz ({student.quizAttempts.length})
              </h3>
            </CardHeader>
            <CardContent>
              {student.quizAttempts.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  No hay intentos de quiz
                </p>
              ) : (
                <div className="space-y-3">
                  {student.quizAttempts.map((attempt) => (
                    <div
                      key={attempt.id}
                      className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg"
                    >
                      <div className="flex-shrink-0">
                        {attempt.status === "PASSED" ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : attempt.status === "FAILED" ? (
                          <XCircle className="h-5 w-5 text-red-500" />
                        ) : (
                          <Clock className="h-5 w-5 text-yellow-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium truncate">
                          {attempt.quiz.title}
                        </h4>
                        <p className="text-xs text-muted-foreground">
                          {attempt.quiz.chapter.course.title} -{" "}
                          {attempt.quiz.chapter.title}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">
                          {attempt.score !== null ? `${attempt.score}%` : "-"}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {attempt.completedAt
                            ? formatDate(new Date(attempt.completedAt))
                            : "En progreso"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <EditStudentModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        student={student}
      />
      <DeactivateStudentModal
        open={deactivateModalOpen}
        onOpenChange={setDeactivateModalOpen}
        studentId={student.id}
        studentName={fullName}
      />
      <EnrollStudentModal
        open={enrollModalOpen}
        onOpenChange={setEnrollModalOpen}
        studentId={student.id}
        studentName={fullName}
        existingCourseIds={existingCourseIds}
      />
    </div>
  );
}
